import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  deliveryState,
  setSelectedDeliveryAction,
} from "../../app/reducers/delivery-slice";
import { useDeleteDeliveryMutation } from "../../query/delivery-query";
import theme from "../../style/theme";
import { Delivery } from "../../types/delivery/delivery";
import { DeliveryStatus } from "../../types/delivery/delivery-status";
import { addressFormatByWorkPlace } from "../../utils/address/address-format";
import { timeFormat, dateFormat } from "../../utils/date/date-format";
import { DeleteButton } from "../button/DeleteButton";
import { BadgeCard } from "../card/BadgeCard";
import { profileState } from "../../app/reducers/profile-slice";
import { isDeliveryWait } from "../../utils/delivery/delivery";
import Checkbox from "@mui/material/Checkbox";
import { useEffect } from "react";
import { styled as muiStyled } from '@mui/material/styles';

interface Props {
  selectedDeliveryIds: string[];
  setSelectedDeliveryIds: (ids: string[]) => void;
  showDateColumn: boolean;
}

// MUI Checkbox 스타일 커스텀
const CustomCheckbox = muiStyled(Checkbox)({
  padding: '0px',
  '& .MuiSvgIcon-root': {
    width: '24px',
    height: '24px'
  }
});

export const DeliveryListTable: React.FC<Props> = ({
  selectedDeliveryIds,
  setSelectedDeliveryIds,
  showDateColumn = false
}) => {
  const baseTableHeaders = [
    "",
    "고객사",
    "대행사",
    "라인명",
    "프로님",
    "상차지",
    "전달지",
    "물품정보",
    "배송상태",
    "",
  ];

  // 날짜 컬럼 추가
  const tableHeaders = showDateColumn
    ? ["", "배송 날짜", ...baseTableHeaders.slice(1)]
    : baseTableHeaders;

  const dispatch = useAppDispatch();
  const { profile } = useAppSelector(profileState);
  const { deliveryPage, selectedDelivery } = useAppSelector(deliveryState);
  const deleteDeliveryMutation = useDeleteDeliveryMutation();

  useEffect(() => {
    // 현재 테이블에 존재하는 delivery ID들
    const currentDeliveryIds = deliveryPage.contents.map(delivery => delivery.id);

    // 선택된 ID들 중 현재 테이블에 존재하는 것들만 필터링
    const filteredIds = selectedDeliveryIds.filter(id =>
      currentDeliveryIds.includes(id)
    );

    // 필터링된 결과가 다르면 업데이트
    if (filteredIds.length !== selectedDeliveryIds.length) {
      setSelectedDeliveryIds(filteredIds);
    }
  }, [deliveryPage.contents]);

  const handleClickTableRow = (
    event: React.MouseEvent<HTMLTableRowElement>
  ) => {
    const selectedDelivery = deliveryPage.contents.find(
      (delivery) => delivery.id === event.currentTarget.id
    );

    if (!selectedDelivery) {
      return;
    }

    dispatch(setSelectedDeliveryAction({ selectedDelivery }));
  };

  const handleClickDeleteDelivery = async (id: string) => {
    if (window.confirm("정말로 삭제하시겠습니까?")) {
      deleteDeliveryMutation.mutate(id);
    } else {
      alert("취소되었습니다.");
    }
  };

  const getDeliveryStatusLabel = (deliveryStatus: DeliveryStatus) => {
    switch (deliveryStatus) {
      case DeliveryStatus.Wait:
        return "배정대기";
      case DeliveryStatus.Matched:
        return "배정완료";
      case DeliveryStatus.Start:
        return "픽업가는중";
      case DeliveryStatus.PickUp:
        return "픽업완료";
      case DeliveryStatus.Ship:
        return "하차가는중";
      default:
        return "배송완료";
    }
  };

  const getDeliveryStatusColor = (deliveryStatus: DeliveryStatus) => {
    switch (deliveryStatus) {
      case DeliveryStatus.Wait:
        return "gray";
      case DeliveryStatus.Matched:
        return "gray";
      case DeliveryStatus.Start:
        return "green";
      case DeliveryStatus.PickUp:
        return "green";
      case DeliveryStatus.Ship:
        return "green";
      default:
        return "blue";
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = deliveryPage.contents.map(delivery => delivery.id);
      setSelectedDeliveryIds(allIds);
    } else {
      setSelectedDeliveryIds([]);
    }
  };

  const handleSelectOne = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    if (event.target.checked) {
      setSelectedDeliveryIds([...selectedDeliveryIds, id]);
    } else {
      setSelectedDeliveryIds(selectedDeliveryIds.filter(deliveryId => deliveryId !== id));
    }
  };

  return (
    <Container>
      <DeliveryTableContainer>
        <Table>
          {tableHeaders.map((tableHeader, index) => (
            <DeliveryTableHeader key={tableHeader || index} index={index}>
              {index === 0 ? (
                <CustomCheckbox
                  checked={selectedDeliveryIds.length === deliveryPage.contents.length}
                  indeterminate={selectedDeliveryIds.length > 0 && selectedDeliveryIds.length < deliveryPage.contents.length}
                  onChange={handleSelectAll}
                />
              ) : (
                tableHeader
              )}
            </DeliveryTableHeader>
          ))}

          <DeliveryTableBody>
            {deliveryPage.contents.map((delivery: Delivery, index: number) => {
              const isSelected = delivery.id === selectedDelivery?.id;

              return (
                <DeliveryTableRow
                  key={delivery.id}
                  id={delivery.id}
                  isSelected={isSelected}
                  onClick={handleClickTableRow}
                >
                  <DeliveryTableData>
                    <CustomCheckbox
                      checked={selectedDeliveryIds.includes(delivery.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectOne(e, delivery.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </DeliveryTableData>
                  {showDateColumn && (
                    <DeliveryTableData>
                      {delivery.dateToWork ? dateFormat(new Date(delivery.dateToWork)) : '-'}
                    </DeliveryTableData>
                  )}
                  <DeliveryTableData>{delivery.client.name}</DeliveryTableData>
                  <DeliveryTableData>{delivery.agency.name}</DeliveryTableData>
                  <DeliveryTableData>
                    {delivery.worker?.scheduleAlias}
                  </DeliveryTableData>
                  <DeliveryTableData>{delivery.worker?.name}</DeliveryTableData>
                  <DeliveryAddressTableData>
                    <FlexContainer>
                      <BoldText>
                        {timeFormat(delivery.pickUpAction.startTimeLimited)}
                      </BoldText>
                      {addressFormatByWorkPlace(delivery.pickUpWorkPlace)}
                    </FlexContainer>
                  </DeliveryAddressTableData>
                  <DeliveryAddressTableData>
                    <FlexContainer>
                      <BoldText>
                        {timeFormat(delivery.dropOffAction.startTimeLimited)}
                      </BoldText>
                      {addressFormatByWorkPlace(delivery.dropOffWorkPlace)}
                    </FlexContainer>
                  </DeliveryAddressTableData>
                  <DeliveryTableData>
                    {delivery.load.name === "null" || delivery.load.name === ""
                      ? "물품 현장 확인"
                      : delivery.load.name}{" "}
                    {delivery.load.quantity}
                  </DeliveryTableData>
                  <DeliveryTableData textAlign="center">
                    <BadgeCard
                      label={getDeliveryStatusLabel(delivery.status)}
                      color={getDeliveryStatusColor(delivery.status)}
                    />
                  </DeliveryTableData>
                  <DeliveryTableData textAlign="center" style={{ paddingRight: "10px", whiteSpace: "nowrap", minWidth: "60px" }}>
                    {(profile?.company.id === delivery.agency.id || isDeliveryWait(delivery)) && (
                      <DeleteButton
                        value={delivery.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (delivery.id) {
                            handleClickDeleteDelivery(delivery.id);
                          }
                        }}
                      />
                    )}
                  </DeliveryTableData>
                </DeliveryTableRow>
              );
            })}
          </DeliveryTableBody>
        </Table>
      </DeliveryTableContainer>
    </Container>
  );
};

const Container = styled.div`
  flex-grow: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const DeliveryTableContainer = styled.div`
  //height: 541px;
  overflow: inherit;
  border-radius: 4px;
  word-break: break-all;
`;
const Table = styled.table`
  width: 100%;
  overflow: hidden;
`;

interface DeliveryTableHeaderProps {
  index: number;
}

const DeliveryTableHeader = styled.th<DeliveryTableHeaderProps>`
  height: 3.188rem;
  text-align: ${(props) => (props.index === 6 ? "center" : "left")}};
  vertical-align: middle;
  background-color: ${theme.palette.primitives.gray50};
  font-style: normal;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.188rem;
  color: ${theme.palette.primitives.gray600};
  padding: ${(props) =>
    props.index === 6 ? "0 0.625rem" : "0 0.625rem 0 1.625rem"}};

  &:first-of-type {
    padding-left: 1rem;
    width: 100px;
  }
  &:nth-last-of-type(1) {
    padding-right: 0px;
  }
`;

const DeliveryTableBody = styled.tbody``;

interface DeliveryTableRowProps {
  isSelected?: boolean;
}

const DeliveryTableRow = styled.tr<DeliveryTableRowProps>`
  height: 3.063rem;
  border-bottom: ${`1px solid ${theme.palette.primitives.gray100}`};
  background-color: ${(props) =>
    props.isSelected
      ? `${theme.palette.primitives.gray50}`
      : `${theme.palette.primitives.white}`};
  cursor: pointer;

  :hover {
    background-color: ${theme.palette.primitives.gray50};
  }
`;

interface DeliveryTableDataProps {
  textAlign?: string;
}

const DeliveryTableData = styled.td<DeliveryTableDataProps>`
  width: 7%;
  padding: 6px 0px;
  text-align: ${(props) => (props.textAlign ? props.textAlign : "left")};
  vertical-align: middle;
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.063rem;
  color: ${theme.palette.primitives.gray600};
  padding: ${(props) =>
    props.textAlign ? "0 0.625rem" : "0 0.625rem 0 1.625rem"}};

  &:first-of-type {
    padding-left: 1rem;
    width: 100px;
  }
  &:nth-last-of-type(1) {
    padding-right: 0px;
  }
`;

const DeliveryAddressTableData = styled.td`
  width: 25%;
  padding: 6px 0px;
  text-align: left;
  vertical-align: middle;
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.063rem;
  color: ${theme.palette.primitives.gray600};
  padding: 0 0.625rem 0 1.625rem;
`;

const FlexContainer = styled.div`
  display: flex;
`;

const BoldText = styled.div`
  font-weight: 700;
  margin-right: 0.5rem;
`;

const DeliveryTableDataLabel = styled.div`
  margin: 0 auto;
  text-align: center;
  vertical-align: middle;
`;
